import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface DescriptionItem {
  readonly term: string;
  readonly value: ReactNode;
  /** For an identifier read character by character: a slug, a key, an address. */
  readonly mono?: boolean;
}

export interface DescriptionListProps {
  readonly items: readonly DescriptionItem[];
  /**
   * `columns` puts the term beside its value, which reads as a record — a tenant's slug, state and
   * region. `stacked` puts it above, for a value too wide or too rich to sit in a column: a row of
   * badges, a paragraph, a list of identities. `inline` runs the pairs along a line and wraps
   * them, for a handful of short facts that are a footnote to something else: a plan's quotas
   * under its name.
   */
  readonly layout?: 'columns' | 'stacked' | 'inline';
  readonly className?: string;
}

/**
 * Facts about one thing, as terms and their values.
 *
 * A real `<dl>`, which is the element that says these are *pairs*: a screen reader announces the
 * list and its length and then reads each term with its value, where the same thing built from
 * divs is a run of unrelated text. Six places across the two applications had built it from
 * scratch — the tenant's record, a plan, the publish panel, a version diff, the settings overview
 * and the contact panel — each with its own spacing and its own idea of how a term should look.
 *
 * The contact panel is why this is a composite and not a page's local helper: **it is the shape
 * the agent's customer pane will be** when M5 gives it a customer to describe.
 *
 * The column layout's grid is the one arbitrary value here, and deliberately: "a label column as
 * wide as its widest label" is a layout structure with no token behind it, and inventing one would
 * make the token set describe this component rather than the design. It is written down once here
 * instead of six times in feature code.
 */
export function DescriptionList({ items, layout = 'columns', className }: DescriptionListProps) {
  return (
    <dl
      className={cn(
        layout === 'columns' &&
          // biome-ignore lint: the grid is a layout structure, explained above.
          'grid grid-cols-[max-content_1fr] gap-x-(--space-lg) gap-y-(--space-sm)',
        layout === 'stacked' && 'flex flex-col gap-(--space-md)',
        layout === 'inline' && 'flex flex-wrap gap-x-(--space-lg) gap-y-(--space-xs)',
        'text-(length:--font-size-body)',
        className,
      )}
    >
      {items.map((item) => (
        /*
         * A `div` is allowed inside a `<dl>` precisely to group a term with its value, and it is
         * what carries the key. `display: contents` then stops it generating a box, so the `dt`
         * and `dd` are the grid's own children — wrapped in anything that *did* generate one,
         * every pair would be a single cell and the column alignment this layout exists for
         * would be gone. The `div` has no semantics of its own, so removing its box removes
         * nothing from the accessibility tree.
         */
        <div
          key={item.term}
          // Inline pairs are the one layout where the group is a box of its own: the term and its
          // value travel together as the line wraps. The others want the `dl`'s own children.
          className={layout === 'inline' ? 'flex gap-(--space-xs)' : 'contents'}
        >
          <dt
            className={cn(
              'text-(--color-text-secondary)',
              layout === 'stacked' && 'text-(length:--font-size-caption)',
            )}
          >
            {item.term}
          </dt>
          <dd
            className={cn(
              'text-(--color-text-primary)',
              /*
               * **A value never widens the panel it is in.** `min-w-0` lets the `1fr` track be
               * narrower than its contents — without it the track's automatic minimum is the
               * value's min-content width — and `wrap-anywhere` is what makes that minimum small:
               * `break-words` breaks a long word when it has to, but does not shrink min-content,
               * so a grid sized by it blows out anyway.
               *
               * This is `Table`'s lesson again (M4.5-11): one `min-w-0` short of a layout that
               * holds. Here it was an email address and a page URL in the agent's chat pane, 244
               * pixels of unbreakable mono in a 287-pixel column, taking the whole document 17
               * pixels sideways at 320 — which a story cannot show, because a component in a
               * canvas is never in the layout that breaks it.
               */
              'min-w-0 wrap-anywhere',
              item.mono === true && 'font-mono',
              layout === 'stacked' && 'mt-(--space-xs)',
            )}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
